import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MenuCategory, MenuCategoriesService } from '../menu-categories.service';
import { MenuCategoryFormDialogComponent } from '../menu-category-form-dialog/menu-category-form-dialog.component';
import { HasPermissionDirective } from '../../../../../core/directives/has-permission.directive';
import { ConfirmationService } from '../../../../../shared/services/confirmation.service';
import { PageHeaderService } from '../../../../../shared/services/page-header.service';
import { SharedModule } from '../../../../../shared/shared.module';

@Component({
  selector: 'app-menu-categories-list',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatDividerModule,
    HasPermissionDirective,
    SharedModule,
  ],
  template: `
    <section class="config-page">
      <app-card variant="default" padding="none" class="config-table-card">
        <div *ngIf="loading" class="loading-container">
          <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
          <p>Loading categories...</p>
        </div>

        <div *ngIf="!loading && items.length === 0" class="empty-state">
          <div class="empty-state__icon"><mat-icon>category</mat-icon></div>
          <h3>No menu categories yet</h3>
          <p>Group your menu into categories like Rice, Pizza, Burger, Beverages.</p>
          <button mat-flat-button color="primary" (click)="onCreate()" *appHasPermission="'menu_category.create'">
            <mat-icon>add</mat-icon> Add Category
          </button>
        </div>

        <div *ngIf="!loading && items.length > 0" class="category-grid">
          <button
            type="button"
            class="category-tile"
            *ngFor="let c of items"
            [class.is-inactive]="!c.is_active"
            (click)="onEdit(c)"
          >
            <div class="category-tile__media">
              <img
                *ngIf="c.image && !failedImages.has(c.id)"
                [src]="c.image"
                [alt]="c.name"
                loading="lazy"
                (error)="onImageError(c)"
              />
              <mat-icon *ngIf="!c.image || failedImages.has(c.id)" class="category-tile__placeholder">category</mat-icon>
              <span
                class="category-tile__status"
                [class.active]="c.is_active"
                [matTooltip]="c.is_active ? 'Active' : 'Inactive'"
              ></span>
              <button
                mat-icon-button
                class="category-tile__menu"
                [matMenuTriggerFor]="menu"
                (click)="$event.stopPropagation()"
                matTooltip="More actions"
                *appHasPermission="'menu_category.update'"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
              <mat-menu #menu="matMenu" class="action-menu">
                <button mat-menu-item (click)="onEdit(c)" *appHasPermission="'menu_category.update'">
                  <mat-icon>edit</mat-icon><span>Edit</span>
                </button>
                <button mat-menu-item (click)="onToggle(c)" *appHasPermission="'menu_category.update'">
                  <mat-icon>{{ c.is_active ? 'toggle_off' : 'toggle_on' }}</mat-icon>
                  <span>{{ c.is_active ? 'Deactivate' : 'Activate' }}</span>
                </button>
                <mat-divider></mat-divider>
                <button mat-menu-item (click)="onDelete(c)" class="danger-action" *appHasPermission="'menu_category.delete'">
                  <mat-icon>delete</mat-icon><span>Delete</span>
                </button>
              </mat-menu>
            </div>
            <div class="category-tile__body">
              <strong class="category-tile__name">{{ c.name }}</strong>
              <span class="category-tile__count">{{ c.items_count ?? 0 }} items</span>
            </div>
          </button>
        </div>

        <mat-paginator
          *ngIf="!loading && items.length > 0"
          [length]="total"
          [pageSize]="pageSize"
          [pageIndex]="pageIndex"
          [pageSizeOptions]="pageSizeOptions"
          [showFirstLastButtons]="true"
          aria-label="Menu category pagination"
          (page)="handlePageEvent($event)"
        ></mat-paginator>
      </app-card>
    </section>
  `,
  styleUrls: ['../../config-list.shared.scss'],
  styles: [
    `
      .category-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
        gap: 1rem;
        padding: 1rem;
      }
      .category-tile {
        display: flex;
        flex-direction: column;
        text-align: left;
        padding: 0;
        border: 1px solid rgba(226, 232, 240, 0.8);
        border-radius: 14px;
        background: #fff;
        overflow: hidden;
        cursor: pointer;
        transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
      }
      .category-tile:hover {
        transform: translateY(-2px);
        border-color: #6366f1;
        box-shadow: 0 8px 20px rgba(79, 70, 229, 0.12);
      }
      .category-tile__media {
        position: relative;
        aspect-ratio: 4 / 3;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
        overflow: hidden;
      }
      .category-tile__media img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
      .category-tile__placeholder {
        font-size: 40px;
        width: 40px;
        height: 40px;
        color: #94a3b8;
      }
      .category-tile__status {
        position: absolute;
        top: 0.625rem;
        left: 0.625rem;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #ef4444;
        border: 2px solid #fff;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
      }
      .category-tile__status.active {
        background: #10b981;
      }
      .category-tile__menu {
        position: absolute;
        top: 0.25rem;
        right: 0.25rem;
        background: rgba(255, 255, 255, 0.9);
        color: #334155;
      }
      .category-tile__menu:hover {
        background: #fff;
      }
      .category-tile__body {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        padding: 0.75rem 0.875rem 0.875rem;
      }
      .category-tile__name {
        font-weight: 600;
        color: #0f172a;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .category-tile__count {
        font-size: 0.8rem;
        color: #64748b;
      }
      .category-tile.is-inactive .category-tile__media {
        filter: grayscale(0.6) opacity(0.7);
      }
    `,
  ],
})
export class MenuCategoriesListComponent implements OnInit, OnDestroy {
  items: MenuCategory[] = [];
  loading = false;
  readonly failedImages = new Set<string>();

  total = 0;
  pageIndex = 0;
  pageSize = 25;
  readonly pageSizeOptions = [10, 25, 50, 100];

  constructor(
    private service: MenuCategoriesService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private pageHeader: PageHeaderService,
  ) {}

  ngOnInit(): void {
    this.pageHeader.set({
      title: 'Menu Categories',
      icon: 'category',
      backRoute: '/dashboard',
      actions: [
        { label: 'Refresh', icon: 'refresh', variant: 'stroked', permission: 'menu_category.read', disabled: () => this.loading, handler: () => this.load() },
        { label: 'Add Category', icon: 'add', variant: 'primary', permission: 'menu_category.create', handler: () => this.onCreate() },
      ],
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeader.clear();
  }

  load(): void {
    this.loading = true;
    this.service.getPage({ page: this.pageIndex + 1, perPage: this.pageSize }).subscribe({
      next: (page) => {
        this.failedImages.clear();
        this.items = page.items;
        this.total = page.meta.total;
        this.pageSize = page.meta.perPage;
        this.pageIndex = Math.max(page.meta.currentPage - 1, 0);
        this.loading = false;
      },
      error: () => {
        this.snackBar.open('Failed to load categories', 'Close', { duration: 3000 });
        this.loading = false;
      },
    });
  }

  onImageError(c: MenuCategory): void {
    this.failedImages.add(c.id);
  }

  handlePageEvent(event: PageEvent): void {
    if (this.pageSize === event.pageSize && this.pageIndex === event.pageIndex) {
      return;
    }
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    this.load();
  }

  onCreate(): void {
    this.dialog.open(MenuCategoryFormDialogComponent, { width: '560px', maxHeight: '90vh', data: {} })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onEdit(c: MenuCategory): void {
    this.dialog.open(MenuCategoryFormDialogComponent, { width: '560px', maxHeight: '90vh', data: { id: c.id } })
      .afterClosed().subscribe((saved) => { if (saved) this.load(); });
  }

  onToggle(c: MenuCategory): void {
    this.service.toggleActive(c.id).subscribe({
      next: (res) => {
        if (res.success) {
          c.is_active = !c.is_active;
          this.snackBar.open(`Category ${c.is_active ? 'activated' : 'deactivated'}`, 'Close', { duration: 3000 });
        }
      },
      error: () => this.snackBar.open('Failed to update status', 'Close', { duration: 3000 }),
    });
  }

  onDelete(c: MenuCategory): void {
    this.confirmationService.confirm({
      title: 'Delete category',
      message: `Are you sure you want to delete "${c.name}"?`,
      confirmText: 'Delete',
      type: 'danger',
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.service.delete(c.id).subscribe({
        next: (res) => {
          if (res.success) {
            this.snackBar.open('Category deleted', 'Close', { duration: 3000 });
            this.load();
          }
        },
        error: (error) => this.snackBar.open(error.error?.message || 'Failed to delete', 'Close', { duration: 5000 }),
      });
    });
  }
}
